import { bool, func, string, oneOfType, number } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import isEmpty from 'lodash/isEmpty';

import BannerPreview, { BannerPreviewList } from 'components/BannerPreview';
import { actions as adminActions } from 'modules/admin';
import { actions as uiActions } from 'modules/ui';
import { selectors as userSelectors } from 'modules/user';
import {
    dismissProductBanner,
    fetchDashboardBanners,
    fetchProductBanners,
    normalizeBannerType,
} from 'utils/banners';
import { isBannerScreenRedesignFeatureEnabled } from 'utils/company';

/**
 * Fetches the live v3 banners a page should show and renders them as
 * `BannerPreview` banners, refreshing the list after a banner is dismissed.
 *
 * Consumers pick the banners they need: pass `product` to show the banners of
 * a single product (e.g. Ask a Pro, Handbooks), or `dashboardVisibility` to
 * show the banners flagged for the homepage regardless of their product. The
 * spacing around the list is left to the consumer through `className`, so each
 * page keeps its own layout.
 *
 * @param  {Object} props The props for the component.
 * @return {JSX.Element}
 */
const LiveBanners = ({
    className,
    companyId,
    dashboardVisibility,
    dismissBannerV3,
    fetchBannersV3,
    isLoading,
    openModal,
    product,
}) => {
    const [banners, setBanners] = useState([]);
    const isFeatureEnabled = isBannerScreenRedesignFeatureEnabled();

    const fetchBanners = useCallback(async () => {
        const activeBanners = dashboardVisibility
            ? await fetchDashboardBanners(fetchBannersV3)
            : await fetchProductBanners(fetchBannersV3, product);

        setBanners(activeBanners);
    }, [dashboardVisibility, fetchBannersV3, product]);

    const onDismissBanner = useCallback(
        banner =>
            dismissProductBanner({
                dismissBannerV3,
                banner,
                isLoading,
                openModal,
                onSuccess: fetchBanners,
            }),
        [dismissBannerV3, fetchBanners, isLoading, openModal]
    );

    useEffect(() => {
        if (isFeatureEnabled && companyId) {
            fetchBanners();
        }
    }, [companyId, fetchBanners, isFeatureEnabled]);

    if (!isFeatureEnabled || isEmpty(banners)) {
        return null;
    }

    return (
        <BannerPreviewList className={className}>
            {banners.map(banner => (
                <BannerPreview
                    key={banner.id}
                    bannerType={normalizeBannerType(banner.bannerType)}
                    message={banner.displayText}
                    dismissible={banner.dismissible}
                    onDismiss={() => onDismissBanner(banner)}
                />
            ))}
        </BannerPreviewList>
    );
};

LiveBanners.propTypes = {
    className: string,
    companyId: oneOfType([number, string]),
    dashboardVisibility: bool,
    dismissBannerV3: func.isRequired,
    fetchBannersV3: func.isRequired,
    isLoading: func.isRequired,
    openModal: func.isRequired,
    product: string,
};

LiveBanners.defaultProps = {
    className: undefined,
    companyId: undefined,
    dashboardVisibility: false,
    product: undefined,
};

const mapDispatchToProps = {
    dismissBannerV3: adminActions.dismissBannerV3,
    fetchBannersV3: adminActions.fetchBannersV3,
    isLoading: uiActions.isLoading,
    openModal: uiActions.openModal,
};

const mapStateToProps = createStructuredSelector({
    companyId: userSelectors.getCompanyId,
});

export { LiveBanners as LiveBannersUnwrapped };
export default connect(mapStateToProps, mapDispatchToProps)(LiveBanners);
